import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../lib/api";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Fetch on mount and whenever `deps` change, aborting the previous request.
 *
 * Aborts are not surfaced as errors: switching symbol quickly should leave the
 * UI showing a spinner for the new request, not an error from the old one.
 */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  enabled = true,
): AsyncState<T> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<T>>({
    data: null, loading: enabled, error: null,
  });
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const ac = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    fnRef
      .current(ac.signal)
      .then((data) => {
        if (!ac.signal.aborted) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || (err as Error)?.name === "AbortError") return;
        setState({ data: null, loading: false, error: err as Error });
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, refetch };
}

/** Run an action on demand, tracking its in-flight and error state. */
export function useAction<Args extends unknown[], T>(
  fn: (...args: Args) => Promise<T>,
): {
  run: (...args: Args) => Promise<T | null>;
  data: T | null;
  loading: boolean;
  error: Error | null;
  reset: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    // Must be set on *every* mount, not just declared true once. React
    // StrictMode mounts, unmounts and remounts in development: with only a
    // cleanup here, the first unmount set this false permanently and every
    // later result and error was silently dropped -- the button just span.
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // `fn` is almost always an inline arrow that closes over the caller's
  // latest state (filter values, form inputs, etc.). Memoising `run` with
  // `[]` and reading `fn` from the closure would freeze all of that at
  // first render -- every later `.run()` call would reuse the original
  // state and silently ignore every filter change. Route through a ref so
  // the latest `fn` always wins without having to make `run`'s identity
  // churn on every render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Monotonically increasing token so stale in-flight calls that resolve
  // after a newer one started don't clobber the user's current filters.
  const seqRef = useRef(0);

  const run = useCallback(
    async (...args: Args) => {
      const seq = ++seqRef.current;
      setLoading(true);
      setError(null);
      try {
        const res = await fnRef.current(...args);
        if (alive.current && seq === seqRef.current) setData(res);
        return res;
      } catch (err) {
        if (alive.current && seq === seqRef.current) setError(err as Error);
        return null;
      } finally {
        if (alive.current && seq === seqRef.current) setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => { setData(null); setError(null); }, []);
  return { run, data, loading, error, reset };
}

/** Debounce a rapidly changing value, for search-as-you-type. */
export function useDebounced<T>(value: T, ms = 220): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Persist a value in localStorage, tolerating a blocked or full store. */
export function usePersisted<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch {
        // Private windows and quota limits are not worth failing a render over.
      }
    },
    [key],
  );
  return [value, set];
}
